import { useState } from 'react';
import { GoArrowLeft, GoArrowRight } from 'react-icons/go';
import { useLanguage } from '@/i18n/LanguageContext';
import ScrollReveal from './ScrollReveal';

const testimonials = [
    {
        quote: 'As a small business owner, managing contracts used to be stressful. With Podus Contract, I can review and draft agreements quickly and confidently. It\u2019s like having a legal team on demand.',
        name: 'Michael Kaizer',
        role: 'General Manager, Research & Insights',
    },
    {
        quote: 'Podus AI completely changed how I handle legal matters. The instant advice feature saved me from a costly landlord dispute. I couldn\u2019t believe how fast and accurate it was.',
        name: 'Amara Osei',
        role: 'Freelance Consultant',
    },
    {
        quote: 'As a law student, having access to AI-powered research tools through Podus has been a game-changer. It helps me prepare cases faster and understand precedents better.',
        name: 'David Mensah',
        role: 'Law Student, University of Ghana',
    },
    {
        quote: 'Our NGO uses Podus to document human rights cases across three countries. The platform has streamlined our workflow and helped us serve more communities effectively.',
        name: 'Fatima Bello',
        role: 'Program Director, Justice First NGO',
    },
    {
        quote: 'With Podus Checker, running background checks on potential business partners is seamless. It gives us the confidence to make informed decisions every time.',
        name: 'Kwame Asante',
        role: 'CEO, TechBridge Solutions',
    },
];

export default function Testimonials() {
    const { t } = useLanguage();
    const [current, setCurrent] = useState(0);
    const [fading, setFading] = useState(false);

    const navigate = (dir: number) => {
        if (fading) return;
        setFading(true);
        setTimeout(() => {
            setCurrent((prev) => (prev + dir + testimonials.length) % testimonials.length);
            setFading(false);
        }, 250);
    };

    const testimonial = testimonials[current];

    return (
        <section className="px-4 py-8 lg:px-16 lg:py-12 xl:px-20">
            <ScrollReveal variant="fade-up" distance={36} duration={800}>
                <div className="rounded-3xl bg-[#F4F4F4] px-6 py-12 lg:px-16 lg:py-16 xl:px-20 xl:py-20">
                    {/* Title */}
                    <ScrollReveal variant="blur-up" delay={100} duration={800}>
                        <h2 className="text-secondary text-[28px] font-extrabold leading-[1.1] tracking-tight sm:text-[36px] lg:text-center lg:text-[56px] xl:text-[64px]">
                            {t('testimonials.heading')}
                        </h2>
                    </ScrollReveal>

                    {/* Quote */}
                    <div className="mt-8 lg:mt-12" style={{ transition: 'opacity 0.25s ease' }}>
                        <blockquote
                            className="text-secondary text-base font-bold leading-[1.7] tracking-tight sm:text-lg lg:text-[28px] xl:text-[32px]"
                            style={{ opacity: fading ? 0 : 1, transition: 'opacity 0.25s ease' }}
                        >
                            <span className="text-secondary/40">{'\u201C'} </span>
                            {t(`testimonials.${current}.quote`)}
                            <span className="text-secondary/40">{'\u201D'}</span>
                        </blockquote>
                    </div>

                    {/* Bottom: Author + Navigation */}
                    <div className="mt-10 flex flex-col gap-8 lg:mt-14 lg:flex-row lg:items-end lg:justify-between">
                        {/* Author Info */}
                        <div style={{ opacity: fading ? 0 : 1, transition: 'opacity 0.25s ease' }}>
                            <p className="text-secondary text-sm font-semibold sm:text-base">
                                {t(`testimonials.${current}.name`)}
                            </p>
                            <p className="text-secondary-100 mt-0.5 text-xs sm:text-sm">
                                {t(`testimonials.${current}.role`)}
                            </p>
                        </div>

                        {/* Navigation */}
                        <div className="flex items-center gap-4">
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="border-secondary/20 hover:border-secondary/50 cursor-pointer rounded-full border px-6 py-3 transition-colors"
                                aria-label="Previous testimonial"
                            >
                                <GoArrowLeft className="text-secondary h-5 w-5" />
                            </button>

                            <span className="min-w-12 select-none text-center text-sm">
                                <span className="text-secondary font-bold">{String(current + 1).padStart(2, '0')}</span>
                                <span className="text-secondary-100">
                                    /{String(testimonials.length).padStart(2, '0')}
                                </span>
                            </span>

                            <button
                                type="button"
                                onClick={() => navigate(1)}
                                className="bg-secondary hover:bg-secondary-400 cursor-pointer rounded-full px-6 py-3 transition-colors"
                                aria-label="Next testimonial"
                            >
                                <GoArrowRight className="h-5 w-5 text-white" />
                            </button>
                        </div>
                    </div>
                </div>
            </ScrollReveal>
        </section>
    );
}
